import React, { useState } from 'react';
import type { Issue } from '../../types/issue';
import { ISSUE_SEVERITY_LABELS } from '../../types/issue';
import type { ControlView, LossView, MeasureDirection } from '../../utils/issueMasp';

const DIRECTION_LABEL: Record<MeasureDirection, string> = {
  higher_is_better: 'Maior é melhor',
  lower_is_better: 'Menor é melhor',
  target_is_best: 'No alvo é melhor',
};

const fieldClass = 'h-8 rounded border border-slate-300 bg-white px-2 text-[12px] dark:border-slate-600 dark:bg-slate-900';

function formatQty(value: number): string {
  return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 4 }).format(value);
}

function parseQty(raw: string): number | null {
  const text = raw.trim();
  if (!text) return null;
  const normalized = text.includes(',') ? text.replace(/\./g, '').replace(',', '.') : text;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

async function run(setError: (value: string) => void, work: () => Promise<void>) {
  setError('');
  try {
    await work();
  } catch (error) {
    setError(error instanceof Error ? error.message : 'Não foi possível salvar.');
  }
}

export const IdentificationWorkspace: React.FC<{
  issue: Issue;
  assigneeName: string;
  losses: LossView[];
  controls: ControlView[];
  editable: boolean;
  pending?: boolean;
  loadError?: boolean;
  onAddLoss?: (input: { name: string; unit: string; value: number }) => Promise<void>;
  onRemoveMeasure?: (id: string) => Promise<void>;
  onSaveControl?: (input: {
    name: string;
    unit: string;
    direction: MeasureDirection;
    baseline: number;
    target: number;
  }) => Promise<void>;
  onDescribePhenomenon?: () => void;
  focusToken?: string | null;
}> = ({
  issue,
  assigneeName,
  losses,
  controls,
  editable,
  pending = false,
  loadError = false,
  onAddLoss,
  onRemoveMeasure,
  onSaveControl,
  focusToken,
}) => {
  const [open, setOpen] = useState<'loss' | 'control' | null>(null);
  const [seenFocus, setSeenFocus] = useState(focusToken);
  if (focusToken && focusToken !== seenFocus) {
    setSeenFocus(focusToken);
    if (focusToken.includes(':loss')) setOpen('loss');
    if (focusToken.includes(':control')) setOpen('control');
  }
  const [error, setError] = useState('');
  const place = [issue.department, issue.process, issue.step].filter(Boolean).join(' · ');
  const showSide = pending || editable || losses.length > 0 || controls.length > 0;

  return (
    <div className="min-w-0">
      <p className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[12px] text-slate-600 dark:text-slate-300">
        <span className="text-[11px] font-semibold tracking-wide text-slate-400">IDENTIFICAÇÃO</span>
        <span>Problema {issue.title.trim() ? '✓' : '—'}</span>
        <span>Perdas {losses.length}</span>
        <span>Item de controle {controls.length > 0 ? '✓' : '—'}</span>
      </p>

      <div className={`mt-3 grid min-w-0 items-start gap-3 ${showSide ? 'xl:grid-cols-2' : ''}`}>
        <section className="min-w-0 rounded-md border border-slate-200 px-3 py-2 dark:border-slate-800">
          <h3 className="text-[11px] font-semibold tracking-wide text-slate-400">Problema</h3>
          <p className="mt-1 text-[15px] font-semibold leading-snug text-slate-950 dark:text-slate-50">{issue.title}</p>
          <p className="mt-1 text-[13px] leading-relaxed text-slate-700 dark:text-slate-200">
            {issue.description?.trim() || 'Sem descrição.'}
          </p>
          <dl className="mt-2 grid gap-1 text-[12px] text-slate-600 dark:text-slate-300 sm:grid-cols-2">
            <div><dt className="text-slate-400">Área / processo / local</dt><dd>{place || 'Não informado'}</dd></div>
            <div><dt className="text-slate-400">Severidade</dt><dd>{ISSUE_SEVERITY_LABELS[issue.severity]}</dd></div>
            <div><dt className="text-slate-400">Responsável</dt><dd>{assigneeName || 'Sem responsável'}</dd></div>
          </dl>
        </section>

        {showSide ? (
          <div className="min-w-0 space-y-3">
            <section className="rounded-md border border-slate-200 px-3 py-2 dark:border-slate-800">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-[11px] font-semibold tracking-wide text-slate-400">Impacto</h3>
                {editable ? (
                  <button type="button" onClick={() => setOpen('loss')} className="text-[12px] font-medium text-slate-700 hover:underline dark:text-slate-200">
                    + Registrar perda
                  </button>
                ) : null}
              </div>
              {pending ? <p className="mt-1 text-[13px] text-slate-500">Carregando perdas.</p> : loadError && losses.length === 0 ? <p className="mt-1 text-[13px] text-slate-500">Não foi possível carregar as perdas.</p> : losses.length === 0 ? <p className="mt-1 text-[13px] text-slate-500">Nenhuma perda registrada.</p> : (
                <ul className="mt-1 divide-y divide-slate-100 dark:divide-slate-800">
                  {losses.map((loss) => (
                    <li key={loss.id} className="flex items-baseline justify-between gap-3 py-1.5 text-[13px]">
                      <span className="min-w-0 truncate font-medium text-slate-900 dark:text-slate-50">{loss.name}</span>
                      <span className="shrink-0 tabular-nums text-slate-600 dark:text-slate-300">
                        {loss.value == null ? 'Sem valor' : `${formatQty(loss.value)} ${loss.unit}`}
                        {editable && onRemoveMeasure ? (
                          <button type="button" onClick={() => void run(setError, () => onRemoveMeasure(loss.id))} className="ml-2 text-[12px] text-slate-500 hover:underline">Excluir</button>
                        ) : null}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {editable && open === 'loss' && onAddLoss ? (
                <LossForm onClose={() => setOpen(null)} onSubmit={(input) => run(setError, async () => { await onAddLoss(input); setOpen(null); })} />
              ) : null}
            </section>

            <section className="rounded-md border border-slate-200 px-3 py-2 dark:border-slate-800">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-[11px] font-semibold tracking-wide text-slate-400">Item de controle</h3>
                {editable && controls.length === 0 ? (
                  <button type="button" onClick={() => setOpen('control')} className="text-[12px] font-medium text-slate-700 hover:underline dark:text-slate-200">
                    + Definir
                  </button>
                ) : null}
              </div>
              {pending ? <p className="mt-1 text-[13px] text-slate-500">Carregando item de controle.</p> : loadError && controls.length === 0 ? <p className="mt-1 text-[13px] text-slate-500">Não foi possível carregar o item de controle.</p> : controls.length === 0 ? <p className="mt-1 text-[13px] text-slate-500">Nenhum item de controle.</p> : controls.map((control) => (
                <div key={control.id} className="mt-1 text-[13px] text-slate-700 dark:text-slate-200">
                  <p className="font-medium text-slate-900 dark:text-slate-50">{control.name}</p>
                  <p className="mt-0.5 text-[12px] text-slate-600 break-words dark:text-slate-300">
                    Baseline {control.baseline == null ? '—' : formatQty(control.baseline)}
                    {' · '}Meta {control.target == null ? '—' : formatQty(control.target)}
                    {' · '}{control.unit}
                    {' · '}{DIRECTION_LABEL[control.direction]}
                  </p>
                  {editable && onRemoveMeasure ? (
                    <button type="button" onClick={() => void run(setError, () => onRemoveMeasure(control.id))} className="mt-1 text-[12px] text-slate-500 hover:underline">Excluir</button>
                  ) : null}
                </div>
              ))}
              {editable && open === 'control' && onSaveControl ? (
                <ControlForm onClose={() => setOpen(null)} onSubmit={(input) => run(setError, async () => { await onSaveControl(input); setOpen(null); })} />
              ) : null}
            </section>
          </div>
        ) : null}
      </div>
      {error ? <p className="mt-2 text-[12px] text-red-700">{error}</p> : null}
    </div>
  );
};

function LossForm({ onSubmit, onClose }: {
  onSubmit: (input: { name: string; unit: string; value: number }) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [unit, setUnit] = useState('');
  const [value, setValue] = useState('');
  const [local, setLocal] = useState('');
  return (
    <form className="mt-2 flex flex-wrap items-center gap-2" onSubmit={(event) => {
      event.preventDefault();
      const parsed = parseQty(value);
      if (!name.trim() || !unit.trim() || parsed == null) {
        setLocal('Informe nome, valor e unidade.');
        return;
      }
      onSubmit({ name, unit, value: parsed });
    }}>
      <input aria-label="Nome da perda" value={name} onChange={(event) => setName(event.target.value)} placeholder="Refugos, horas, custo" className={`${fieldClass} min-w-0 flex-1`} />
      <input aria-label="Valor da perda" value={value} onChange={(event) => setValue(event.target.value)} placeholder="Valor" className={`${fieldClass} w-24`} />
      <input aria-label="Unidade da perda" value={unit} onChange={(event) => setUnit(event.target.value)} placeholder="un, h, R$" className={`${fieldClass} w-24`} />
      <button type="submit" className="h-8 rounded border border-slate-300 px-2 text-[12px] font-medium text-slate-700">Salvar</button>
      <button type="button" onClick={onClose} className="h-8 px-1 text-[12px] text-slate-500">Cancelar</button>
      {local ? <p className="basis-full text-[12px] text-red-700">{local}</p> : null}
    </form>
  );
}

function ControlForm({ onSubmit, onClose }: {
  onSubmit: (input: { name: string; unit: string; direction: MeasureDirection; baseline: number; target: number }) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [unit, setUnit] = useState('');
  const [baseline, setBaseline] = useState('');
  const [target, setTarget] = useState('');
  const [direction, setDirection] = useState<MeasureDirection>('lower_is_better');
  const [local, setLocal] = useState('');
  return (
    <form className="mt-2 grid gap-2 sm:grid-cols-2" onSubmit={(event) => {
      event.preventDefault();
      const base = parseQty(baseline);
      const goal = parseQty(target);
      if (!name.trim() || !unit.trim() || base == null || goal == null) {
        setLocal('Informe indicador, baseline, meta e unidade.');
        return;
      }
      onSubmit({ name, unit, direction, baseline: base, target: goal });
    }}>
      <input aria-label="Indicador" value={name} onChange={(event) => setName(event.target.value)} placeholder="Indicador" className={fieldClass} />
      <input aria-label="Unidade do indicador" value={unit} onChange={(event) => setUnit(event.target.value)} placeholder="Unidade" className={fieldClass} />
      <input aria-label="Baseline" value={baseline} onChange={(event) => setBaseline(event.target.value)} placeholder="Baseline" className={fieldClass} />
      <input aria-label="Meta" value={target} onChange={(event) => setTarget(event.target.value)} placeholder="Meta" className={fieldClass} />
      <select aria-label="Direção de melhoria" value={direction} onChange={(event) => setDirection(event.target.value as MeasureDirection)} className={fieldClass}>
        {(Object.keys(DIRECTION_LABEL) as MeasureDirection[]).map((key) => (
          <option key={key} value={key}>{DIRECTION_LABEL[key]}</option>
        ))}
      </select>
      <div className="flex items-center gap-2">
        <button type="submit" className="h-8 rounded border border-slate-300 px-2 text-[12px] font-medium text-slate-700">Salvar</button>
        <button type="button" onClick={onClose} className="h-8 px-1 text-[12px] text-slate-500">Cancelar</button>
      </div>
      {local ? <p className="sm:col-span-2 text-[12px] text-red-700">{local}</p> : null}
    </form>
  );
}
